<script lang="ts">
import { defineBlock } from '@bagelink/blox'
import { defineSchema, $ } from '@bagelink/vue'
const defaults = {
	navigationTitle: 'Explore Topics:',
	sections: [],
}
const schema = defineSchema({
	navigationTitle: $.text('Navigation Title'),
	sections: $.array('Sections', defineSchema({
		id: $.text('ID'),
		title: $.text('Title'),
		type: $.select('Type', [
			{ label: 'Featured', value: 'featured' },
			{ label: 'Articles', value: 'articles' },
			{ label: 'Topics', value: 'topics' },
		]),
		featuredTitle: $.text('Featured Title'),
		featuredSubtitle: $.text('Featured Subtitle'),
		featuredImage: $.upload('Featured Image'),
		featuredLink: $.text('Featured Link'),
		articles: $.array('Articles', defineSchema({
			title: $.text('Title'),
			image: $.upload('Image'),
			link: $.text('Link'),
			authors: $.array('Authors', defineSchema({
				name: $.text('Name'),
				title: $.text('Title'),
				image: $.upload('Image'),
			}), { collapsible: true }),
		}), { collapsible: true }),
		topics: $.array('Topics', defineSchema({
			name: $.text('Name'),
			link: $.text('Link'),
		}), { collapsible: true }),
	}), { collapsible: true }),
})
export const BlockExploreTopics = defineBlock({ pickerHidden: true, thumbnail: '/block-thumbs/BlockExploreTopics.svg', category: 'Content', order: 220, name: 'BlockExploreTopics', label: 'Explore Topics', icon: 'explore', schema, defaults })
</script>
<script setup lang="ts">

import { Image } from '@bagelink/vue'
import { computed } from 'vue'
import AuthorSmallLink from '@/components/blocks/AuthorSmallLink.vue'
import { articleTitleText, sanitizeArticleTitleHtml } from '@/utils/articleHtml'
import { displayImage } from '@/utils/image'




interface Author {
	name: string
	title: string
	image?: string
	slug?: string
	link?: string
}

interface Article {
	title: string
	authors: Author[]
	image: string
	link: string
}

interface Topic {
	name: string
	link: string
}

interface Section {
	id: string
	title: string
	type: string
	articles?: Article[]
	topics?: Topic[]
	featuredTitle?: string
	featuredSubtitle?: string
	featuredImage?: string
	featuredLink?: string
}

const props = defineProps<{
	sections?: Section[]
	navigationTitle?: string
}>()

const visibleSections = computed(() => (props.sections ?? []).filter(section => {
	if (section.type === 'featured') return !!section.featuredLink
	if (section.type === 'articles') return !!section.articles?.length
	if (section.type === 'topics') return !!section.topics?.length
	return false
}))

function scrollToSection(sectionId: string) {
	if (typeof document !== 'undefined') {
		const element = document.getElementById(sectionId)
		if (element) {
			element.scrollIntoView({ behavior: 'smooth' })
		}
	}
}
</script>

<template>
	<div v-if="visibleSections.length" class="w1100px grid grid-wrap-4 relative gap-2 pb-3 pt-1 m_py-2 m_px-1-5">
		<!-- Sidebar Navigation -->
		<aside class="bg-gray px-1-5 sticky-3-75 py-1 m_none">
			<h3 class="txt24 regular my-0 pb-1">
				{{ navigationTitle }}
			</h3>
			<nav class="display-flex column gap-1 light txt17">
				<a
					v-for="section in visibleSections"
					:key="section.id"
					class="redhover"
					@click="scrollToSection(section.id)"
				>
					{{ section.title }}
				</a>
			</nav>
		</aside>

		<!-- Main Content -->
		<main class="grid-span-3 -mt-3-5 mt-0">
			<!-- Each Section -->
			<section
				v-for="section in visibleSections"
				:id="section.id"
				:key="section.id"
				class="pt-3-5"
			>
				<!-- Section Title -->
				<h3 v-if="section.type !== 'featured'" class="bg-gray m-0 px-1 py-075 light txt24 m_txt-center">
					{{ section.title }}
				</h3>

				<!-- Featured Article (Large Square) -->
				<router-link v-if="section.type === 'featured' && section.featuredLink" :to="section.featuredLink" class="bg-gray flex decoration-none w-100p hover m_column m_flex-stretch1">
					<Image :src="displayImage(section.featuredImage, 'card')" :alt="section.featuredTitle" ratio="5-4" class=" w230px aspect-ratio-5-4 m_w1000px cover m_aspect-ratio-16-9" lazy />
					<div class="px-2 m_p-1-5">
						<span class="color-red">{{ section.featuredSubtitle }}</span>
						<h3 class="txt28 m_txt24 regular my-0 line-height-15 ellipsis-3">
							{{ section.featuredTitle }}
						</h3>
					</div>
				</router-link>

				<!-- Articles Grid -->
				<div v-else-if="section.type === 'articles'" class="grid grid-wrap-2 gap-2 px-1 pt-2 -mb-2 m_mb-0">
					<article
						v-for="article in section.articles"
						:key="article.link"
						class="hover"
					>
						<router-link :to="article.link" class="decoration-none flex gap-1 align-items-top">
							<Image :src="displayImage(article.image || '/og.jpg', 'thumb')" :alt="articleTitleText(article.title)" ratio="5-4" class="h60px aspect-ratio-5-4 cover mt-025" lazy />
							<div>
								<h4 class="txt17 m-0 regular pb-025" v-html="sanitizeArticleTitleHtml(article.title)" />
								<div class="article-authors">
									<AuthorSmallLink
										v-for="author in article.authors "
										:key="author.name"
										:author="author"
										:show-image="false"
									/>
								</div>
							</div>
						</router-link>
					</article>
				</div>

				<!-- Topics Grid -->
				<div v-else-if="section.type === 'topics'" class="grid grid-wrap-2 gap-1 px-2 pt-1-5">
					<router-link
						v-for="topic in section.topics"
						:key="topic.link"
						:to="topic.link"
						class="decoration-none txt17 redhover"
					>
						{{ topic.name }}
					</router-link>
				</div>
			</section>
		</main>
	</div>
</template>

<style>
.sticky-3-75,
.position-sticky-3-75 {
    position: sticky !important;
    top: 3.75rem;
    align-self: start;
}
</style>
